import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Briefcase, Building2, Phone, MapPin, FileText, Loader2, CheckCircle, Clock, XCircle, Percent } from 'lucide-react';
import { apiRequest } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export default function ProPage() {
    const navigate = useNavigate();
    const { user, checkAuth } = useAuth();
    
    const [loading, setLoading] = useState(false);
    const [proStatus, setProStatus] = useState(null);
    const [formData, setFormData] = useState({
        company_name: '',
        siret: '',
        phone: '',
        address: '',
        description: ''
    });

    useEffect(() => {
        if (user) {
            fetchProStatus();
        }
    }, [user]);

    const fetchProStatus = async () => {
        try {
            const data = await apiRequest('/pro/status');
            setProStatus(data);
        } catch (error) {
            console.error('Failed to fetch pro status:', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.company_name || !formData.siret || !formData.phone || !formData.address) {
            toast.error('Veuillez remplir tous les champs obligatoires');
            return;
        }

        setLoading(true);
        try {
            await apiRequest('/pro/request', {
                method: 'POST',
                body: JSON.stringify(formData)
            });
            toast.success('Demande envoyée avec succès !');
            await fetchProStatus();
            await checkAuth();
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    if (!user) {
        return (
            <div className="min-h-screen pt-20 pb-12" data-testid="pro-page">
                <div className="max-w-4xl mx-auto px-4 text-center py-20">
                    <Briefcase className="h-16 w-16 text-muted-foreground/50 mx-auto mb-4" />
                    <h2 className="font-heading text-2xl font-bold mb-2">Espace Professionnels</h2>
                    <p className="text-muted-foreground mb-6">
                        Connectez-vous pour accéder à l'espace professionnel.
                    </p>
                    <Button className="rounded-full" onClick={() => navigate('/auth')}>
                        Se connecter
                    </Button>
                </div>
            </div>
        );
    }

    // Already a pro
    if (proStatus?.is_pro) {
        return (
            <div className="min-h-screen pt-20 pb-12" data-testid="pro-page">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-8">
                        <div className="flex items-center justify-center gap-3 mb-4">
                            <Briefcase className="h-10 w-10 text-primary" />
                            <h1 className="font-heading text-3xl sm:text-4xl font-bold">Espace Pro</h1>
                        </div>
                    </div>

                    <Card className="glass rounded-3xl max-w-2xl mx-auto">
                        <CardContent className="py-12 text-center">
                            <div className="h-20 w-20 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-6">
                                <CheckCircle className="h-10 w-10 text-emerald-500" />
                            </div>
                            <Badge className="bg-emerald-500/10 text-emerald-500 border-0 mb-4">
                                Compte Professionnel Actif
                            </Badge>
                            <h2 className="font-heading text-2xl font-bold mb-2">
                                {proStatus.company_name}
                            </h2>
                            <div className="flex items-center justify-center gap-2 text-2xl font-bold text-primary mb-6">
                                <Percent className="h-6 w-6" />
                                <span>{proStatus.discount_percentage}% de remise</span>
                            </div>
                            <p className="text-muted-foreground mb-6">
                                Votre remise professionnelle est automatiquement appliquée sur toutes vos commandes.
                            </p>
                            <div className="flex gap-4 justify-center">
                                <Button className="rounded-full" onClick={() => navigate('/shop')}>
                                    Voir la boutique
                                </Button>
                                <Button variant="secondary" className="rounded-full" onClick={() => navigate('/orders')}>
                                    Mes commandes
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    // Pending request
    if (proStatus?.pro_status === 'pending') {
        return (
            <div className="min-h-screen pt-20 pb-12" data-testid="pro-page">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-8">
                        <div className="flex items-center justify-center gap-3 mb-4">
                            <Briefcase className="h-10 w-10 text-primary" />
                            <h1 className="font-heading text-3xl sm:text-4xl font-bold">Espace Pro</h1>
                        </div>
                    </div>

                    <Card className="glass rounded-3xl max-w-2xl mx-auto">
                        <CardContent className="py-12 text-center">
                            <div className="h-20 w-20 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto mb-6">
                                <Clock className="h-10 w-10 text-amber-500" />
                            </div>
                            <Badge className="bg-amber-500/10 text-amber-500 border-0 mb-4">
                                Demande en cours de traitement
                            </Badge>
                            <h2 className="font-heading text-xl font-bold mb-2">
                                Votre demande a bien été reçue
                            </h2>
                            <p className="text-muted-foreground mb-6">
                                Notre équipe examine votre dossier. Vous recevrez une réponse rapidement.
                            </p>
                            <Button variant="secondary" className="rounded-full" onClick={() => navigate('/')}>
                                Retour à l'accueil
                            </Button>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    // Rejected request
    if (proStatus?.pro_status === 'rejected') {
        return (
            <div className="min-h-screen pt-20 pb-12" data-testid="pro-page">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-8">
                        <div className="flex items-center justify-center gap-3 mb-4">
                            <Briefcase className="h-10 w-10 text-primary" />
                            <h1 className="font-heading text-3xl sm:text-4xl font-bold">Espace Pro</h1>
                        </div>
                    </div>

                    <Card className="glass rounded-3xl max-w-2xl mx-auto">
                        <CardContent className="py-12 text-center">
                            <div className="h-20 w-20 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-6">
                                <XCircle className="h-10 w-10 text-red-500" />
                            </div>
                            <Badge className="bg-red-500/10 text-red-500 border-0 mb-4">
                                Demande refusée
                            </Badge>
                            <h2 className="font-heading text-xl font-bold mb-2">
                                Votre demande n'a pas été acceptée
                            </h2>
                            <p className="text-muted-foreground mb-6">
                                N'hésitez pas à nous contacter pour plus d'informations.
                            </p>
                            <Button variant="secondary" className="rounded-full" onClick={() => navigate('/')}>
                                Retour à l'accueil
                            </Button>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    // Request form
    return (
        <div className="min-h-screen pt-20 pb-12" data-testid="pro-page">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="flex items-center justify-center gap-3 mb-4">
                        <Briefcase className="h-10 w-10 text-primary" />
                        <h1 className="font-heading text-3xl sm:text-4xl font-bold">Espace Professionnels</h1>
                    </div>
                    <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                        Vous êtes pisciniste, technicien ou revendeur ? Bénéficiez de remises exclusives sur tous nos produits.
                    </p>
                </div>

                <div className="grid lg:grid-cols-3 gap-8">
                    {/* Benefits */}
                    <div className="space-y-4">
                        <h2 className="font-heading text-xl font-semibold">Avantages Pro</h2>
                        <Card className="glass rounded-2xl">
                            <CardContent className="p-4">
                                <div className="flex items-start gap-3">
                                    <Percent className="h-5 w-5 text-primary mt-0.5" />
                                    <div>
                                        <h3 className="font-semibold">Remises exclusives</h3>
                                        <p className="text-sm text-muted-foreground">Jusqu'à 30% de réduction sur tous les produits</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card className="glass rounded-2xl">
                            <CardContent className="p-4">
                                <div className="flex items-start gap-3">
                                    <Building2 className="h-5 w-5 text-primary mt-0.5" />
                                    <div>
                                        <h3 className="font-semibold">Compte dédié</h3>
                                        <p className="text-sm text-muted-foreground">Suivi de commandes et historique détaillé</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card className="glass rounded-2xl">
                            <CardContent className="p-4">
                                <div className="flex items-start gap-3">
                                    <Phone className="h-5 w-5 text-primary mt-0.5" />
                                    <div>
                                        <h3 className="font-semibold">Support prioritaire</h3>
                                        <p className="text-sm text-muted-foreground">Ligne directe avec notre équipe technique</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Form */}
                    <div className="lg:col-span-2">
                        <Card className="glass rounded-3xl">
                            <CardHeader>
                                <CardTitle>Demande de compte professionnel</CardTitle>
                                <CardDescription>Remplissez le formulaire pour obtenir votre compte pro</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <form onSubmit={handleSubmit} className="space-y-6">
                                    <div className="grid sm:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="company_name">Nom de l'entreprise *</Label>
                                            <div className="relative">
                                                <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                                <Input
                                                    id="company_name"
                                                    value={formData.company_name}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                                                    placeholder="Ma Société SARL"
                                                    className="pl-10 h-12 rounded-xl"
                                                    data-testid="pro-company"
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="siret">N° SIRET *</Label>
                                            <div className="relative">
                                                <FileText className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                                <Input
                                                    id="siret"
                                                    value={formData.siret}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, siret: e.target.value }))}
                                                    placeholder="123 456 789 00012"
                                                    className="pl-10 h-12 rounded-xl"
                                                    data-testid="pro-siret"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="phone">Téléphone professionnel *</Label>
                                        <div className="relative">
                                            <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                            <Input
                                                id="phone"
                                                value={formData.phone}
                                                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                                                placeholder="01 23 45 67 89"
                                                className="pl-10 h-12 rounded-xl"
                                                data-testid="pro-phone"
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="address">Adresse de l'entreprise *</Label>
                                        <div className="relative">
                                            <MapPin className="absolute left-4 top-3 h-4 w-4 text-muted-foreground" />
                                            <Input
                                                id="address"
                                                value={formData.address}
                                                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                                                placeholder="123 Rue de la Piscine, 75001 Paris"
                                                className="pl-10 h-12 rounded-xl"
                                                data-testid="pro-address"
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="description">Activité / Description</Label>
                                        <Textarea
                                            id="description"
                                            value={formData.description}
                                            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                            placeholder="Décrivez votre activité (pisciniste, revendeur, technicien...)"
                                            className="min-h-[100px] rounded-xl"
                                            data-testid="pro-description"
                                        />
                                    </div>

                                    <Button
                                        type="submit"
                                        className="w-full rounded-full h-12 font-semibold"
                                        disabled={loading}
                                        data-testid="pro-submit"
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                                Envoi en cours...
                                            </>
                                        ) : (
                                            'Envoyer ma demande'
                                        )}
                                    </Button>
                                </form>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        </div>
    );
}
